import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';

type NewLabelProps = {
  modelId: string;
  name: string;
  description?: string;
  parentLabelId?: string;
};

type UpdateLabelProps = {
  modelId: string;
  labelId: string;
  name?: string;
  userDescription?: string;
  optimizedDescription?: string;
  parentLabelId?: string | null;
};

type DeleteLabelProps = {
  modelId: string;
  labelId: string;
};

// API response type for label endpoints
type LabelResponse = {
  classificationModelId: string;
  id: string;
  name: string;
  key: string;
  path: string;
  userDescription: string;
  optimizedDescription: string;
  parentLabelId: string | null;
  createdAt: string;
  updatedAt: string;
};

type UseMutateClassificationLabelOptions = {
  onSuccess?: () => void;
};

export function useMutateClassificationLabel(options?: UseMutateClassificationLabelOptions) {
  const queryClient = useQueryClient();
  const { orgFetch, currentOrg, role } = useOrg();
  const { errorToast, toast } = useToast();

  const singleModelQueryKey = (modelId: string) => [
    'next-gen',
    'classification-models',
    modelId,
    genQueryKey(currentOrg.id),
  ];

  const newLabel = useMutation({
    mutationFn: async ({ modelId, name, description, parentLabelId }: NewLabelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot create labels');
      }
      const res = await orgFetch<LabelResponse>(`/api/v2/next-gen-models/classification/${modelId}/labels`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          description: description || '',
          parentLabelId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error('Unable to create label');
      }
      return data;
    },
    onSuccess: (data: LabelResponse) => {
      // Invalidate the model query to refetch labels
      queryClient.invalidateQueries({ queryKey: singleModelQueryKey(data.classificationModelId) });
      toast({
        title: 'Label created',
        description: `Label "${data.name}" has been created`,
      });
      mixpanel.track('Next-Gen Classification Label Created');
      options?.onSuccess?.();
    },
    onError: () => {
      errorToast('Failed to create label');
    },
  });

  const updateLabel = useMutation({
    mutationFn: async ({ modelId, labelId, ...data }: UpdateLabelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot update labels');
      }
      const res = await orgFetch<LabelResponse>(`/api/v2/next-gen-models/classification/${modelId}/labels/${labelId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error('Label update failed');
      }
      return responseData;
    },
    onSuccess: (data: LabelResponse) => {
      // Invalidate the model query to refetch labels
      queryClient.invalidateQueries({ queryKey: singleModelQueryKey(data.classificationModelId) });
      mixpanel.track('Next-Gen Classification Label Updated');
    },
    onError: () => {
      errorToast('Failed to update label');
    },
  });

  const deleteLabel = useMutation({
    mutationFn: async ({ modelId, labelId }: DeleteLabelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot delete labels');
      }
      const res = await orgFetch<{ message: string }>(
        `/api/v2/next-gen-models/classification/${modelId}/labels/${labelId}`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) {
        throw new Error('Unable to delete label');
      }
      return { modelId, labelId };
    },
    onSuccess: ({ modelId }) => {
      // Invalidate the model query to refetch labels
      queryClient.invalidateQueries({ queryKey: singleModelQueryKey(modelId) });
      toast({
        title: 'Label deleted',
        description: 'The label has been deleted',
      });
      mixpanel.track('Next-Gen Classification Label Deleted');
    },
    onError: () => {
      errorToast('Failed to delete label');
    },
  });

  return {
    newLabel,
    updateLabel,
    deleteLabel,
  };
}
